import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { api, formatCurrency } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Users, TrendingUp, AlertTriangle, CheckCircle, Clock,
  DollarSign, Shield, Activity, ArrowUpRight, ArrowDownRight,
  Bell, Settings, RefreshCw, Filter, Download, MessageSquare, HandCoins
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';

const COLORS = ['#EA580C', '#16A34A', '#2563EB', '#7C3AED', '#DC2626', '#CA8A04'];
const RISK_COLORS = {
  excellent: '#16A34A',
  good: '#22C55E', 
  moderate: '#CA8A04',
  high: '#EA580C',
  critical: '#DC2626'
};

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [adminStats, setAdminStats] = useState(null);
  const [tontines, setTontines] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [contributionTrend, setContributionTrend] = useState([]);
  const [memberDistribution, setMemberDistribution] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [tontineHealth, setTontineHealth] = useState(null);
  const [selectedHealthTontineId, setSelectedHealthTontineId] = useState('');
  const [exportPriority, setExportPriority] = useState('all');

  const loadAdminData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsResult, tontinesResult, alertsResult, healthResult] = await Promise.allSettled([
        api.getAdminStats(),
        api.getAdminTontines(),
        api.getAdminRiskAlerts(),
        api.getAdminTontineHealth()
      ]);

      if (statsResult.status !== 'fulfilled') {
        throw statsResult.reason;
      }

      const statsData = statsResult.value?.data || {};
      const tontinesData = tontinesResult.status === 'fulfilled' ? (tontinesResult.value?.data?.tontines || []) : [];
      const alertsData = alertsResult.status === 'fulfilled' ? (alertsResult.value?.data?.alerts || []) : [];
      const healthData = healthResult.status === 'fulfilled'
        ? (healthResult.value?.data || null)
        : {
            global_score: 0,
            at_risk_members: [],
            trend_30_days: [],
            recommended_actions: [],
            default_tontine_id: tontinesData[0]?.id || null,
            catchup_plan_summary: { proposed: 0, accepted: 0, in_progress: 0, failed: 0, total: 0 },
          };

      setAdminStats(statsData);
      setTontines(tontinesData);
      setRiskAlerts(alertsData);
      setContributionTrend(statsData.contribution_trend || []);
      setMemberDistribution(statsData.member_distribution || []);
      setRecentActivity(statsData.recent_activity || []);
      setPendingActions(statsData.pending_actions || []);
      setTontineHealth(healthData);

      setSelectedHealthTontineId((prev) => {
        const availableIds = new Set((tontinesData || []).map((item) => item?.id).filter(Boolean));
        if (prev && availableIds.has(prev)) {
          return prev;
        }
        const fallback = healthData?.default_tontine_id || tontinesData?.[0]?.id || '';
        return fallback;
      });

      if ((statsData?.total_tontines || 0) === 0) {
        navigate('/dashboard');
        return;
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error(t('adminDashboard.actions.loadError'));
      setAdminStats({
        total_tontines: 0,
        total_members: 0,
        total_collected: 0,
        pending_contributions: 0,
        pending_count: 0,
        high_risk_members: 0,
        avg_participation_rate: 0,
      });
      setTontineHealth({
        global_score: 0,
        at_risk_members: [],
        trend_30_days: [],
        recommended_actions: [],
        default_tontine_id: null,
        catchup_plan_summary: { proposed: 0, accepted: 0, in_progress: 0, failed: 0, total: 0 },
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  const handleHealthAction = async (actionType) => {
    try {
      const targetTontineId = selectedHealthTontineId || tontineHealth?.default_tontine_id || tontines?.[0]?.id;
      if (!targetTontineId) {
        toast.error(t('adminDashboard.health.noTontine'));
        return;
      }

      const payload = {
        action: actionType,
        tontine_id: targetTontineId,
      };

      if (actionType === 'group_message') {
        payload.message = t('adminDashboard.health.groupMessageTemplate');
      }

      const response = await api.runAdminTontineHealthAction(payload);
      toast.success(response.data?.message || t('common.success'));
      await loadAdminData();
    } catch (error) {
      toast.error(t('adminDashboard.health.actionError'));
    }
  };

  const loadMockData = () => {
    // Generate mock data for demonstration
    const mockTrend = Array.from({ length: 30 }, (_, i) => ({
      date: format(subDays(new Date(), 29 - i), 'dd/MM'),
      amount: Math.floor(Math.random() * 500000) + 100000,
      count: Math.floor(Math.random() * 20) + 5
    }));

    const mockDistribution = [
      { name: t('adminDashboard.memberDistribution.active'), value: 45, color: COLORS[0] },
      { name: t('adminDashboard.memberDistribution.pending'), value: 12, color: COLORS[1] },
      { name: t('adminDashboard.memberDistribution.inactive'), value: 8, color: COLORS[2] }
    ];

    setContributionTrend(mockTrend);
    setMemberDistribution(mockDistribution);
    setAdminStats({
      total_tontines: 5,
      total_members: 65,
      total_collected: 2750000,
      pending_contributions: 450000,
      high_risk_members: 3,
      avg_participation_rate: 87.5
    });
  };

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const displayedAtRiskMembers = (tontineHealth?.at_risk_members || []).filter((member) => {
    if (!selectedHealthTontineId) {
      return true;
    }
    return member.tontine_id === selectedHealthTontineId;
  });

  const catchupPlanSummary = tontineHealth?.catchup_plan_summary || {
    proposed: 0,
    accepted: 0,
    in_progress: 0,
    failed: 0,
    total: 0,
  };

  const handleExportPendingActions = async () => {
    try {
      const priorityParam = exportPriority === 'all' ? null : exportPriority;
      const response = await api.exportAdminPendingActionsCsv(priorityParam);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = priorityParam ? `_${priorityParam}` : '';
      link.download = `admin_pending_actions${suffix}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('adminDashboard.actions.exportSuccess'));
    } catch (error) {
      console.error('Error exporting pending actions CSV:', error);
      toast.error(t('adminDashboard.actions.exportError'));
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendUp, color = 'orange' }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-stone-500">{title}</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-${color}-100`}>
            <Icon className={`h-5 w-5 text-${color}-600`} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
            {trendUp ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
            <span>{trend}</span>
          </div>
        )}
      </CardContent>
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-${color}-500 to-${color}-400`} />
    </Card>
  );

  const RiskAlertCard = ({ alert }) => (
    <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 hover:border-orange-300 transition-colors">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${RISK_COLORS[alert.level]}20` }}>
          <AlertTriangle className="h-5 w-5" style={{ color: RISK_COLORS[alert.level] }} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium text-stone-900 break-words">{alert.member_name}</p>
          <p className="text-sm text-stone-500 break-words">{alert.tontine_name}</p>
          <p className="text-xs text-stone-500 break-words">{alert.reason}</p>
          <div className="pt-2 flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              style={{
                borderColor: RISK_COLORS[alert.level],
                color: RISK_COLORS[alert.level]
              }}
            >
              {t('adminDashboard.risk.scoreLabel')}: {alert.score}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-3"
              onClick={() => navigate(`/tontines/${alert.tontine_id}`)}
            >
              {t('common.view')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-medium text-stone-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' && entry.value > 1000 
                ? formatCurrency(entry.value, 'XOF') 
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-display font-bold text-stone-900">
              {t('adminDashboard.title')}
            </h1>
            <p className="text-stone-500 mt-1">
              {t('adminDashboard.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadAdminData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('adminDashboard.actions.refresh')}
            </Button>
            <Select value={exportPriority} onValueChange={setExportPriority}>
              <SelectTrigger className="w-[180px]" data-testid="admin-export-priority-select">
                <SelectValue placeholder="Priorité export" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorités</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExportPendingActions} data-testid="admin-export-btn">
              <Download className="h-4 w-4 mr-2" />
              {t('common.export')}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title={t('adminDashboard.stats.managedTontines')}
            value={adminStats?.total_tontines || 0}
            subtitle={t('adminDashboard.stats.managedTontinesSubtitle')}
            icon={Users}
            color="orange"
          />
          <StatCard
            title={t('adminDashboard.stats.totalCollected')}
            value={formatCurrency(adminStats?.total_collected || 0, 'XOF')}
            subtitle={t('adminDashboard.stats.thisMonth')}
            icon={DollarSign}
            trend="+12.5%"
            trendUp={true}
            color="green"
          />
          <StatCard
            title={t('adminDashboard.stats.pendingContributions')}
            value={formatCurrency(adminStats?.pending_contributions || 0, 'XOF')}
            subtitle={t('adminDashboard.stats.pendingMembers', { count: adminStats?.pending_count || 0 })}
            icon={Clock}
            color="yellow"
          />
          <StatCard
            title={t('adminDashboard.stats.riskAlerts')}
            value={adminStats?.high_risk_members || 0}
            subtitle={t('adminDashboard.stats.membersToWatch')}
            icon={AlertTriangle}
            color="red"
          />
        </div>

        {/* Main Content */}
        <Card className="mb-6" data-testid="admin-tontine-health">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-500" />
              {t('adminDashboard.health.title')}
            </CardTitle>
            <CardDescription>{t('adminDashboard.health.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <div className="rounded-xl border border-stone-200 p-4 bg-stone-50">
                <p className="text-sm text-stone-500">{t('adminDashboard.health.globalScore')}</p>
                <p className="text-2xl font-bold text-stone-900">{Number(tontineHealth?.global_score || 0).toFixed(1)} / 100</p>
                <p className="text-xs text-stone-500 mt-1">{t('adminDashboard.health.atRiskCount', { count: tontineHealth?.at_risk_members?.length || 0 })}</p>
              </div>
              <div className="rounded-xl border border-stone-200 p-4 bg-stone-50 lg:col-span-2">
                <p className="text-sm text-stone-500 mb-2">{t('adminDashboard.health.trend30Days')}</p>
                <div className="h-[140px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={tontineHealth?.trend_30_days || []}>
                      <defs>
                        <linearGradient id="healthScoreArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#EA580C" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#EA580C" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="score" name={t('adminDashboard.health.scoreAxis')} stroke="#EA580C" fill="url(#healthScoreArea)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-xl border border-stone-200 p-4">
                <p className="text-sm font-medium text-stone-700 mb-3">{t('adminDashboard.health.recommendedActions')}</p>
                <div className="mb-3">
                  <p className="text-xs text-stone-500 mb-1">{t('adminDashboard.health.targetTontine')}</p>
                  <Select value={selectedHealthTontineId} onValueChange={setSelectedHealthTontineId}>
                    <SelectTrigger className="w-full sm:w-[320px]" data-testid="health-target-tontine-select">
                      <SelectValue placeholder={t('adminDashboard.health.selectTontine')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(tontines || []).map((tontine) => (
                        <SelectItem key={tontine.id} value={tontine.id}>
                          {tontine.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => handleHealthAction('send_reminder')} data-testid="health-cta-reminder" disabled={!selectedHealthTontineId}>
                    <Bell className="h-4 w-4 mr-2" />
                    {t('adminDashboard.health.ctaRemind')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleHealthAction('catchup_plan')} data-testid="health-cta-catchup" disabled={!selectedHealthTontineId}>
                    <HandCoins className="h-4 w-4 mr-2" />
                    {t('adminDashboard.health.ctaCatchup')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleHealthAction('group_message')} data-testid="health-cta-group-message" disabled={!selectedHealthTontineId}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {t('adminDashboard.health.ctaGroupMessage')}
                  </Button>
                </div>
                <div className="mt-4 border-t border-stone-200 pt-3">
                  <p className="text-xs text-stone-500 mb-2">{t('adminDashboard.health.catchupStatusTitle')}</p>
                  <div className="flex flex-wrap gap-2" data-testid="catchup-status-summary">
                    <Badge variant="outline" data-testid="catchup-status-proposed">{t('adminDashboard.health.catchupProposed')}: {catchupPlanSummary.proposed || 0}</Badge>
                    <Badge variant="outline" data-testid="catchup-status-accepted">{t('adminDashboard.health.catchupAccepted')}: {catchupPlanSummary.accepted || 0}</Badge>
                    <Badge variant="outline" data-testid="catchup-status-progress">{t('adminDashboard.health.catchupInProgress')}: {catchupPlanSummary.in_progress || 0}</Badge>
                    <Badge variant="outline" data-testid="catchup-status-failed">{t('adminDashboard.health.catchupFailed')}: {catchupPlanSummary.failed || 0}</Badge>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-stone-200 p-4">
                <p className="text-sm font-medium text-stone-700 mb-3">{t('adminDashboard.health.topAtRisk')}</p>
                <div className="space-y-2 max-h-[180px] overflow-auto">
                  {displayedAtRiskMembers.slice(0, 5).map((member, index) => (
                    <div key={`${member.member_id || 'member'}-${index}`} className="flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-stone-900">{member.member_name}</p>
                        <p className="truncate text-stone-500">{member.tontine_name}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0">{member.score}</Badge>
                    </div>
                  ))}
                  {displayedAtRiskMembers.length === 0 && (
                    <p className="text-sm text-stone-500">{t('adminDashboard.health.noRiskMembers')}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Charts Column - 2/3 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contribution Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-500" />
                  {t('adminDashboard.charts.contributionTrend')}
                </CardTitle>
                <CardDescription>{t('adminDashboard.charts.last30Days')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={contributionTrend}>
                      <defs>
                        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#EA580C" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#EA580C" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#E7E5E4' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#E7E5E4' }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        name={t('adminDashboard.charts.amount')}
                        stroke="#EA580C"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorAmount)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Participation by Tontine */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-orange-500" />
                  {t('adminDashboard.charts.participationByTontine')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tontines.slice(0, 6).map(t => ({
                      name: t.name?.substring(0, 15) || t('adminDashboard.tontineFallbackName'),
                      participation: t.participation_rate || Math.floor(Math.random() * 30) + 70,
                      members: t.current_members || 0
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="participation" 
                        name={t('adminDashboard.charts.participation')}
                        fill="#EA580C" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side Column - 1/3 */}
          <div className="space-y-6">
            {/* Member Distribution Pie */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-orange-500" />
                  {t('adminDashboard.memberDistribution.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={memberDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {memberDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Risk Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-red-500" />
                  {t('adminDashboard.risk.title')}
                </CardTitle>
                <CardDescription>{t('adminDashboard.risk.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] overflow-y-auto pr-2" data-testid="admin-risk-alerts-list">
                  <div className="space-y-3 pb-1">
                    {riskAlerts.length > 0 ? (
                      riskAlerts.map((alert, index) => (
                        <RiskAlertCard key={index} alert={alert} />
                      ))
                    ) : (
                      // Mock alerts for demo
                      [
                        { member_name: 'Amadou Diallo', tontine_name: 'Tontine Famille', score: 35, level: 'critical', reason: t('adminDashboard.risk.mock.consecutiveLate'), tontine_id: '1' },
                        { member_name: 'Fatou Ndiaye', tontine_name: 'Épargne Mensuelle', score: 52, level: 'high', reason: t('adminDashboard.risk.mock.partialPayment'), tontine_id: '2' },
                        { member_name: 'Moussa Sow', tontine_name: 'Groupe Dakar', score: 68, level: 'moderate', reason: t('adminDashboard.risk.mock.irregularity'), tontine_id: '3' }
                      ].map((alert, index) => (
                        <RiskAlertCard key={index} alert={alert} />
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tontines Quick View */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminDashboard.quickView.title')}</CardTitle>
                <CardDescription>{t('adminDashboard.quickView.subtitle')}</CardDescription>
              </div>
              <Button variant="outline" onClick={() => navigate('/tontines')}>
                {t('dashboardPage.viewAll')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(tontines.length > 0 ? tontines : [
                { id: '1', name: 'Tontine Famille', current_members: 8, max_members: 10, total_collected: 800000, currency: 'XOF', status: 'active' },
                { id: '2', name: 'Épargne Mensuelle', current_members: 12, max_members: 12, total_collected: 1200000, currency: 'XOF', status: 'active' },
                { id: '3', name: 'Groupe Dakar', current_members: 5, max_members: 8, total_collected: 250000, currency: 'XOF', status: 'pending' }
              ]).slice(0, 6).map((tontine) => (
                <div 
                  key={tontine.id}
                  className="p-4 bg-stone-50 rounded-xl border border-stone-200 hover:border-orange-300 cursor-pointer transition-all hover:shadow-md"
                  onClick={() => navigate(`/tontines/${tontine.id}`)}
                  data-testid={`admin-tontine-${tontine.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-stone-900 truncate">{tontine.name}</h3>
                    <Badge variant={tontine.status === 'active' ? 'default' : 'secondary'}>
                      {tontine.status === 'active' ? t('tontine.statuses.active') : t('tontine.statuses.pending')}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-500">{t('tontine.members')}</span>
                      <span className="font-medium">{tontine.current_members}/{tontine.max_members}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">{t('adminDashboard.quickView.collected')}</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(tontine.total_collected, tontine.currency)}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full bg-stone-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all"
                        style={{ width: `${(tontine.current_members / tontine.max_members) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
  );
}
